// Lou step-up (client side). React#158 Inc2c: when the API signals
// `louStepUpRequired` (an editor/admin who needs a per-user Salesforce context,
// set by dbServer ValidateOrg when LOU_STEPUP_ENABLED is on), kick off Lou's
// silent step-up through the dbServer broker (`GET /auth/lou/begin`) and hand the
// browser to the returned Salesforce authorize URL. This replaces the brittle
// personal-OAuth popup for the editor/admin path; org-wide Help viewing is never
// interrupted. Additive and self-healing: if Lou is not configured yet the begin
// endpoint returns 503 and we fall back to the existing flow.

import Axios from "axios";

const apiBase = () => "https://" + process.env.REACT_APP_API;

// Build the identity params /auth/lou/begin expects, from the embedder's query
// string and/or the user object the API returned. The LWC passes UserId /
// OrganizationId / RoleId / ProfileId / email on the iframe URL; the user object
// carries them as userId / orgId / roleId / Email / baseUrl.
export function louIdentityParams(query, user) {
  const p = {};
  if (query) {
    if (query.UserId) p.UserId = query.UserId;
    if (query.OrganizationId) p.OrganizationId = query.OrganizationId;
    if (query.RoleId != null) p.RoleId = query.RoleId;
    if (query.ProfileId) p.ProfileId = query.ProfileId;
    if (query.email) p.email = query.email;
  }
  if (user) {
    if (!p.UserId && user.userId) p.UserId = user.userId;
    if (!p.OrganizationId && user.orgId) p.OrganizationId = user.orgId;
    if (p.RoleId == null && user.roleId != null) p.RoleId = user.roleId;
    if (!p.email && user.Email) p.email = user.Email;
    // The org instance URL lets Lou resolve the user's My Domain host.
    if (user.baseUrl) p.InstitutionURL = user.baseUrl;
  }
  return p;
}

// True if the API told us this user needs a Lou step-up.
export function needsLouStepUp(user) {
  return !!(user && user.louStepUpRequired);
}

// Session-scoped guard so a single completed step-up doesn't re-trigger on the
// post-redirect reload (the MySQL UserToken is still null until the token-storage
// migration, so the API may still report louStepUpRequired; this breaks the loop
// for the session).
const STEP_GUARD = "lou_stepped_up";
export function hasSteppedUp() {
  try {
    return window.sessionStorage.getItem(STEP_GUARD) === "1";
  } catch (e) {
    return false;
  }
}
export function markSteppedUp() {
  try {
    window.sessionStorage.setItem(STEP_GUARD, "1");
  } catch (e) {
    /* sessionStorage unavailable (private mode / iframe) -> best effort */
  }
}

// Read the ?lou= flag the dbServer callback adds on its redirect back.
export function louReturnFlag() {
  try {
    return new URLSearchParams(window.location.search).get("lou"); // "success" | "error" | null
  } catch (e) {
    return null;
  }
}

// After the redirect, mint a fresh short-lived access token from the
// cookie-authenticated Lou session endpoint. Returns the token or null. Also the
// hook for silent refresh on a timer.
export async function getLouSession() {
  try {
    const res = await Axios.get(apiBase() + "/auth/lou/session", {
      withCredentials: true,
    });
    return (res && res.data && res.data.accessToken) || null;
  } catch (e) {
    return null;
  }
}

// Initiate the step-up. Resolves true if the browser is being redirected to
// Salesforce (the caller should stop its normal login flow), false if no
// step-up happened (Lou not configured, not required, or an error) so the caller
// can continue with the legacy flow.
export async function startLouStepUp({ query, user, action = "author-read", returnTo } = {}) {
  try {
    const params = louIdentityParams(query, user);
    params.action = action;
    if (returnTo) params.returnTo = returnTo;
    const res = await Axios.get(apiBase() + "/auth/lou/begin", {
      params,
      withCredentials: true,
    });
    if (res && res.data && res.data.required && res.data.authorizeUrl) {
      // Hand off to Salesforce. The dbServer callback sets the hardened
      // (HttpOnly/Secure/SameSite=None/Partitioned) refresh cookie and returns
      // the session, then redirects back to the app.
      window.location.assign(res.data.authorizeUrl);
      return true;
    }
    return false;
  } catch (err) {
    // 503 (Lou not configured) or any error: fall back to the legacy flow.
    return false;
  }
}
